import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "classic" | "dark" | "warm" | "energetic" | "spiritual" | "sepia";

export const THEMES: Theme[] = ["classic", "dark", "warm", "energetic", "spiritual", "sepia"];

export const THEME_LABELS: Record<Theme, string> = {
  classic:   "Classic",
  dark:      "Dark",
  warm:      "Warm",
  energetic: "Energetic",
  spiritual: "Spiritual",
  sepia:     "Sepia",
};

export const THEME_DESCRIPTIONS: Record<Theme, string> = {
  classic:   "Clean stone neutrals",
  dark:      "Deep charcoal night",
  warm:      "Amber & terracotta",
  energetic: "Electric indigo",
  spiritual: "Deep violet mystic",
  sepia:     "Aged parchment",
};

export const THEME_SWATCHES: Record<Theme, { bg: string; card: string; text: string }> = {
  classic:   { bg: "#e7e5e4", card: "#ffffff", text: "#1c1917" },
  dark:      { bg: "#141210", card: "#1e1a18", text: "#f5f4f1" },
  warm:      { bg: "#ede3d5", card: "#faf4eb", text: "#1a0f07" },
  energetic: { bg: "#12103a", card: "#1e1b54", text: "#ededfc" },
  spiritual: { bg: "#0f0620", card: "#1e0d38", text: "#f0ecfc" },
  sepia:     { bg: "#e8dfc8", card: "#f8f3e8", text: "#1a1007" },
};

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem("theme") as Theme | null;
    if (stored && (THEMES as string[]).includes(stored)) return stored;
  } catch {
    // ignore
  }
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "classic";
}

function applyTheme(theme: Theme) {
  if (theme === "classic") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.setAttribute("data-theme", theme);
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  function setTheme(t: Theme) {
    setThemeState(t);
    try {
      localStorage.setItem("theme", t);
    } catch {
      // ignore
    }
    applyTheme(t);
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
